import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, CheckCircle, ShieldCheck, ArrowSquareOut, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Aegis Universal USDT Vault — one contract, one address, any chain.
 *
 * Props:
 *  - vault: { family, label, address, chains: [...], note }
 *  - featured: bool — enlarges the card + shows Contract-Verified badge (used for the EVM primary vault)
 */
export default function UniversalVaultCard({ vault, featured = false }) {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(vault.address);
            setCopied(true);
            toast.success(`${vault.label} address copied`);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            // Clipboard permission denied (headless / iframe / user-denied) — graceful fallback
            try {
                const ta = document.createElement("textarea");
                ta.value = vault.address;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                setCopied(true);
                toast.success(`${vault.label} address copied`);
                setTimeout(() => setCopied(false), 1600);
            } catch {
                toast.error("Copy failed — long-press the address to copy manually");
            }
        }
    };

    return (
        <div
            data-testid={`vault-card-${vault.family}`}
            className={`card relative overflow-hidden ${featured ? "p-6 sm:p-8" : "p-5"}`}
            style={
                featured
                    ? {
                          background:
                              "linear-gradient(135deg, rgba(37,99,235,0.10) 0%, rgba(0,229,255,0.04) 60%, transparent 100%)",
                          borderColor: "var(--primary-soft-border, rgba(37,99,235,0.35))",
                      }
                    : undefined
            }
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--primary-glow)]">
                            {vault.label}
                        </div>
                        {featured && (
                            <span
                                data-testid="vault-verified-badge"
                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.12em] px-1.5 py-0.5 rounded"
                                style={{
                                    background: "rgba(34,197,94,0.14)",
                                    color: "#22c55e",
                                    border: "1px solid rgba(34,197,94,0.35)",
                                }}
                            >
                                <ShieldCheck size={11} weight="fill" /> Contract Verified
                            </span>
                        )}
                    </div>
                    <h3 className={`font-bold text-[var(--text)] mt-1 tracking-[-0.02em] ${featured ? "text-[22px] sm:text-[26px]" : "text-[16px]"}`}>
                        {featured ? "Universal USDT Deposit Vault" : vault.label}
                    </h3>
                    <p className="text-[12.5px] text-[var(--text-2)] mt-1 leading-[1.55] max-w-lg">
                        {vault.note}
                    </p>
                </div>

                {/* QR (only on featured card to keep the grid tight on secondary vaults) */}
                {featured && (
                    <div
                        className="p-2 rounded-md shrink-0"
                        style={{ background: "#fff" }}
                        data-testid="vault-qr"
                    >
                        <QRCodeSVG value={vault.address} size={104} level="M" includeMargin={false} />
                    </div>
                )}
            </div>

            {/* Address row */}
            <div
                className="flex items-center gap-2 p-3 rounded-md"
                style={{ background: "var(--bg-raised)", border: "1px solid var(--border)" }}
            >
                <code
                    data-testid={`vault-address-${vault.family}`}
                    className={`font-mono text-[var(--text)] break-all flex-1 ${featured ? "text-[13.5px]" : "text-[12.5px]"}`}
                >
                    {vault.address}
                </code>
                <button
                    data-testid={`vault-copy-${vault.family}`}
                    onClick={copy}
                    className="p-2 rounded-md hover:bg-[var(--bg-surface)] shrink-0 transition-colors"
                    aria-label={`Copy ${vault.label} address`}
                >
                    {copied ? <CheckCircle size={14} weight="fill" className="text-[#22c55e]" /> : <Copy size={14} />}
                </button>
            </div>

            {/* Accepted chains */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mr-1">
                    Accepts USDT from
                </span>
                {vault.chains.map((c) => (
                    <span
                        key={c.id}
                        data-testid={`vault-chain-${c.id}`}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full"
                        style={{
                            background: "var(--bg-raised)",
                            border: "1px solid var(--border-strong)",
                            color: "var(--text)",
                        }}
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary-glow)]" />
                        {c.network}
                        <span className="text-[10px] text-[var(--text-3)]">· min ${c.min_deposit_usdt}</span>
                    </span>
                ))}
            </div>

            {/* Auto-credit footer */}
            {featured && (
                <div
                    className="mt-5 flex items-start gap-2 p-3 rounded-md"
                    style={{
                        background: "rgba(0,229,255,0.06)",
                        border: "1px solid rgba(0,229,255,0.22)",
                    }}
                >
                    <Lightning size={14} weight="fill" className="text-[var(--primary-glow)] shrink-0 mt-0.5" />
                    <div className="text-[12px] text-[var(--text-2)] leading-[1.55]">
                        <span className="text-[var(--text)] font-medium">Auto-credit enabled.</span>{" "}
                        USDT sent from any listed chain is auto-detected on-chain and credited to your
                        Aegis USDT balance within ~90 seconds. No manual tx-hash needed.
                        <a
                            href="#fasttrack"
                            className="ml-1.5 inline-flex items-center gap-0.5 text-[var(--primary-bright)] hover:underline"
                        >
                            Fast-track it <ArrowSquareOut size={11} />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}
